/**
 * Pet Finder Management Page
 * - Lists lost and found pet reports
 * - Supports pagination and status tracking
 * - Allows viewing and managing pet finder entries
 * - Export functionality for pet finder data
 */

import React, { useEffect } from "react";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPetFinders } from "../../redux/slices/petFinderSlice";
import TableComponent from "../../components/common/TableComponent";
import { FiDownload } from "react-icons/fi";
import { formatDateTime } from "../../components/details/Shared/helpers";

export default function PetFinder() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const petFinderItems = useSelector((state) => state.petFinder.items);
  const status = useSelector((state) => state.petFinder.status);
  const pagination = useSelector((state) => state.petFinder.pagination);

  useEffect(() => {
    dispatch(fetchPetFinders(pagination.currentPage));
  }, [dispatch, pagination.currentPage]);

  const handlePageChange = (page) => {
    dispatch(fetchPetFinders(page));
  };

  // Status badge styles for Pet Finder
  const statusClasses = {
    Active: "bg-green-100 text-green-800",
    Inactive: "bg-red-100 text-red-800",
    Pending: "bg-yellow-50 text-yellow-600",
    Default: "bg-gray-100 text-gray-600",
  };

  // Table columns
  const columns = [
    { key: "pet", label: "Pet" },
    { key: "location", label: "Location" },
    { key: "name", label: "Posted By" },
    { key: "reward", label: "Reward" },
    { key: "submitted", label: "Posted On", render: (val) => formatDateTime(val) },
    {
      key: "status",
      label: "Status",
      render: (val) => (
        <span
          className={`rounded-md px-3 py-1 text-xs font-semibold ${
            statusClasses[val] || statusClasses.Default
          }`}
        >
          {val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()}
        </span>
      ),
    },
  ];



  // Row click handler
  const handleRowClick = (row) => {
    navigate(`/swift-app/admin/pet-finder/${row.uuid}`);
  };

  // Navigate to ExportPage
  // const handleExportClick = () => {
  //   navigate(`/swift-app/admin/export?tab=PetFinder`);
  // };

  return (
    <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
      {/* ✅ Header */}
      <div className="flex flex-col gap-4">
        {/* Top Row — Back button + Title + Export */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go Back"
              className="p-2 md:p-3 text-lg md:text-xl text-gray-800 hover:text-blue-600"
            >
              <MdOutlineArrowBackIos />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              Pet Finder
            </h1>
          </div>

         {/* ✅ Responsive Export button */}
             {/* Icon always visible */}
          {/* <button
            onClick={handleExportClick}
            className="flex items-center justify-center gap-2 bg-blue-500 text-white font-medium px-3 sm:px-4 md:px-5 py-2 rounded-lg shadow hover:bg-blue-600 transition"
          >
         
            <FiDownload className="w-5 h-5" />
            <span className="hidden lg:inline">Export</span>
          </button> */}
        </div>

        {/* Subtitle */}
        <p className="text-gray-600 text-sm sm:text-base ml-10 md:ml-14">
          Manage lost & found pet listings
        </p>
      </div>

      {/* ✅ Table */}
      <div className="mt-8 md:mt-10 overflow-x-auto">
        {status === "loading" ? (
          <div className="text-center text-gray-500 py-10 text-sm md:text-base">
            Loading...
          </div>
        ) : (
          <TableComponent
            columns={columns}
            data={petFinderItems}
            onRowClick={handleRowClick}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
